import { CatalogueService } from 'src/app/angularJS/services/catalogue/catalogue.service';
import { ACTToolbarService } from 'src/app/angularJS/services/toolbar.service';
import { UserMessagesService } from 'src/app/angularJS/services/user-messages.service';
import { PermissionHelperService } from 'src/app/angularJS/services/permissionHelpers.service';
import { DialogService } from 'src/app/angularJS/services/dialog/dialog.service';
import { UserService } from 'src/app/angularJS/services/user/user.service';
import { LinkBuilder } from 'src/app/angularJS/services/link-builder.service';
import { FileExporterService } from 'src/app/angularJS/services/file-exporting/file-exporter.service';
import { FileReaderService } from 'src/app/angularJS/services/file-reading/file-reader.service';

declare const JSZip;

/**
 * A controller used to view a list of catalogues
 */
export class CatalogueListController {
  public static $inject = [
    '$rootScope',
    '$state',
    'catalogueService',
    'PrimaryAction',
    'ACTToolbarService',
    'UserMessages',
    'permissionHelpers',
    'dialogService',
    '$translate',
    'userService',
    'LinkBuilder',
    'catalogueStatusConstants',
    'fileExporterService',
    'fileReaderService'
  ];

  public paginationData: InterACT.Interfaces.IPaginationData;
  public catalogues: Array<InterACT.Interfaces.Catalogues.Catalogue>;

  constructor(
    private $rootScope,
    private $state: angular.ui.IStateService,
    private catalogueService: CatalogueService,
    private primaryAction,
    private ActToolbarService: ACTToolbarService,
    private UserMessages: UserMessagesService,
    private permissionHelpers: PermissionHelperService,
    private dialogService: DialogService,
    private $translate: ng.translate.ITranslateService,
    private userService: UserService,
    private linkBuilder: LinkBuilder,
    private catalogueStatusConstants: InterACT.Interfaces.ICatalogueStatusConstants,
    private fileExporterService: FileExporterService,
    private fileReaderService: FileReaderService
  ) {
    this.activate();
  }

  /**
   * Gets a list of catalogues
   *
   * @param page The number of the page of data to be retrieved
   * @param perPage The number of items per page to be retrieved
   * @returns the promise from the catalogueService
   */
  public fetchCollection = (page?: number, perPage?: number) => {
    this.$rootScope.showSpinner = true;
    this.setPageData(page, perPage);

    var activeSchemeLinks = this.userService.selectedSchemes.map(x=>this.userService.convertSchemeToLink(x).link);
    return this.catalogueService.getList(
      this.paginationData.currentPage,
      this.paginationData.perPageCount,
      false,
      activeSchemeLinks
    ).then((resource) => {
      this.catalogues = resource.catalogues;
      this.paginationData.totalCount = resource.meta.totalCount;
      this.$rootScope.showSpinner = false;
    });
  }

  /**
   * Gets permsissions for a given catalogue
   * @param catalogue The catalogue we wish to get permissions for
   */
  public getPermissionsForCatalogue(catalogue: InterACT.Interfaces.Catalogues.Catalogue) {
    return this.permissionHelpers.getPermissionsForCatalogue(catalogue);
  }

  /**
   * A method which is used to delete a catalogue
   *
   * @param catalogue The catalogue we wish to delete
   */
  public deleteCatalogue = (catalogue: InterACT.Interfaces.Catalogues.Catalogue) => {
    return this.UserMessages.buildConfirmDialog(
      'CONFIRM_DIALOG.CATALOGUE.DELETE_THIS',
      'CONFIRM_DIALOG.CATALOGUE.DELETE',
      { catalogue: catalogue.scheme.abbreviation },
      'CONFIRM_DIALOG.CATALOGUE.DELETE_CONFIRM'
    ).then(() => {
      this.catalogueService.remove(catalogue.id).then(() => {
        this.UserMessages.showToast('TOAST_MESSAGE.CATALOGUE.DELETED');
        this.fetchCollection();
      });
    });
  }

  /**
   * A method which is used to create a draft catalogue
   *
   * @param catalogue The catalogue we wish to create with a status of Draft
   */
  public createDraftCatalogue = (catalogue: InterACT.Interfaces.Catalogues.Catalogue) => {
    return this.UserMessages.buildConfirmDialog(
      'CONFIRM_DIALOG.CATALOGUE.CREATE_DRAFT',
      'CONFIRM_DIALOG.CATALOGUE.DRAFT',
      { catalogue: catalogue.scheme.abbreviation },
      'CONFIRM_DIALOG.CATALOGUE.CREATE_DRAFT_CONFIRM'
    ).then(() => {
      this.catalogueService.edit(catalogue.id, <any>{ status: this.catalogueStatusConstants.DRAFT }).then(() => {
        this.UserMessages.showToast('TOAST_MESSAGE.CATALOGUE.DRAFT_CREATED');
        this.fetchCollection();
      });
    });
  }

  /**
   * Exports a copy of the current version of the catalogue to zip file with the name
   * as the catalogue's scheme, description and version
   * @param catalogue the catalogue to export
   */
  public exportCatalogue = (catalogue: InterACT.Interfaces.Catalogues.Catalogue) => {
    const fileName = `${catalogue.scheme.abbreviation}-${catalogue.description}-${catalogue.version}`;
    this.$rootScope.showSpinner = true;
    this.catalogueService.getCatalogue(catalogue.id, { expand: true }).then((response: InterACT.Interfaces.Catalogues.Catalogue) => {
      this.$rootScope.showSpinner = false;
      this.fileExporterService.downloadZipFile(fileName, `${fileName}.json`, angular.toJson(response));
    });
  }

  /**
   * A method that changes to the copy state for the entered catalogue
   * @param catalogue the catalogue to go to copy state for
   */
  public goToCopy = (catalogue: InterACT.Interfaces.Catalogues.Catalogue) => {
    this.$state.go('catalogues.copy', { sourceCatalogue: catalogue, catalogueId: catalogue.id });
  }

  /**
   * A method that changes to the edit state for the entered catalogue
   * @param catalogue the catalogue to go to edit state for
   */
  public goToEdit = (catalogue: InterACT.Interfaces.Catalogues.Catalogue) => {
    this.$state.go('catalogues.edit', { sourceCatalogue: catalogue, catalogueId: catalogue.id });
  }

  /**
   * A method that changes to the catalogue dashboard state for the entered catalogue
   * @param catalogue the catalogue to view
   */
  public goToCatalogue = (catalogue: InterACT.Interfaces.Catalogues.Catalogue) => {
    this.$state.go('catalogue.dashboard', { catalogueId: catalogue.id, catalogueVersion: catalogue.version });
  }

  /**
   * A method which is used to take the user to the create screen
   */
  private goToCreate = () => {
    this.$state.go('catalogues.create');
  };

  /**
   * A method which is used to instantiate our controller
   */
  private activate() {
    this.catalogues = [];
    this.paginationData = {
      currentPage: 1,
      perPageCount: 15
    };
    this.ActToolbarService.getToolbar('primary').then((toolbar) => {
      toolbar.setDefaults('CATALOGUES');
    });
    this.primaryAction.setActions([{
      name: 'Create',
      icon: 'add',
      action: this.goToCreate
    }, {
      name: 'Import',
      icon: 'cloud_upload',
      action: this.importCatalogue
    }]);
  }

  /**
   * This function is used to set the paginator data for requests
   *
   * @param page the new current page
   * @param perPage the new per page count
   */
  private setPageData = (page?: number, perPage?: number) => {
    if (!_.isUndefined(page)) {
      this.paginationData.currentPage = angular.isNumber(page) ? page : 1;
    }
    this.paginationData.perPageCount = angular.isNumber(perPage) ? perPage : this.paginationData.perPageCount;
  }

  /**
   * Calls the dialog service to show an import file dialog. Taking the file it then reads catalogue data
   * and sends it in a create action to the catalogue service with new version and the current scheme.
   */
  private importCatalogue = () => {
    this.dialogService.showImportFileDialog(
      this.$translate.instant('CATALOGUE_DETAIL.CATALOGUE_IMPORT'),
      ['.zip', '.json']
    ).then((file) => {
      this.fileReaderService.readDataFromFile(file).then((data: Object) => {
        let catalogue: InterACT.Interfaces.Catalogues.Catalogue;
        try {
          catalogue = <InterACT.Interfaces.Catalogues.Catalogue>data;
          if (!catalogue.description) {
            throw new Error();
          }
        }
        catch (e) {
          return this.UserMessages.showToast('TOAST_MESSAGE.CATALOGUE.IMPORT_FAILED');
        }
        catalogue = this.setCatalogueToDraft(catalogue);
        this.$rootScope.showSpinner = true;
        this.catalogueService.create(catalogue).then(() => {
          this.$rootScope.showSpinner = false;
          this.UserMessages.showToast('TOAST_MESSAGE.CATALOGUE.IMPORTED', { name: catalogue.description });
          this.fetchCollection();
        });
      }).catch(() => {
        return this.UserMessages.showToast('TOAST_MESSAGE.CATALOGUE.IMPORT_FAILED');
      });
    });
  }

  /**
   * Sets the scheme, status and version of the catalogue back to their initial state for creation
   * @param catalogue to catalogue to change
   * @returns the altered catalogue
   */
  private setCatalogueToDraft = (catalogue: InterACT.Interfaces.Catalogues.Catalogue) => {
    catalogue.scheme = {
      abbreviation: this.userService.selectedScheme.reference,
      link: this.linkBuilder.getSchemesLink(this.userService.selectedScheme.id)
    };
    catalogue.status = this.catalogueStatusConstants.DRAFT;
    delete catalogue.id;
    catalogue.version = 1;
    return catalogue;
  }
}
